<template>
  <div
    v-if="alert"
    class="alert-widget card h-100"
    :class="!dataForCategoryDetailsWidget && 'border-0'"
  >
    <div v-if="dataForCategoryDetailsWidget">
      <WidgetHeader
        :title="$tr(dataForCategoryDetailsWidget.messages, 'title')"
        :onBack="back"
      />
      <WidgetBody
        :id="id"
        :onSwiperInitCallback="onSwiperInit"
        :viewMode="viewMode"
      >
        <template #slide-0>
          <CategoryDetails
            :id="id"
            :category="dataForCategoryDetailsWidget"
            :surveyName="surveyName"
            :shouldHideOverviewForCategory="true"
            :userIsAdminForCurrentGroupOrSuperAdmin="
              userIsGroupAdmin || userIsPulsAdmin
            "
            :theme="theme"
            selectedTab="questions"
            :onSelectCategoryTab="onSelectCategoryTab"
            :isIndividualGroup="isIndividualGroup"
            :userInfo="userInfo"
            :selectedGroup="selectedGroup"
            :loadingQuestionResponses="loadingQuestionResponses"
            :loadQuestionResponses="onLoadQuestionResponses"
          />
        </template>
      </WidgetBody>
    </div>
    <div
      v-else
      :class="`alert alert-dismissible alert-box-${alert.type}-${theme} p-5 mb-0 h-100`"
    >
      <div class="d-flex flex-column justify-content-between h-100">
        <div>
          <div class="d-flex align-items-center justify-content-between ps-5">
            <div class="d-flex align-items-center">
              <i
                :class="`bi bi-${alert.icon} icon-${alert.type}-${theme} fs-3 me-4 `"
              ></i>
              <h4 class="mb-0 card-title">{{ alert.alert }}</h4>
            </div>
            <button
              type="button"
              class="btn btn-icon remove-fullscreen"
              data-bs-dismiss="alert"
            >
              <i class="bi bi-x fs-1"></i>
            </button>
          </div>

          <div class="p-5">
            <div class="d-flex mt-3 pr-3">
              <div class="w-100">
                <p v-if="alert.feedback != 'null'" class="fs-5">
                  {{ alert.feedback }}
                </p>
                <p v-if="alert.detailed != 'null'" class="fs-5 pr-5">
                  {{ alert.detailed }}
                </p>
              </div>
            </div>
            <div v-if="shouldDisplayActions" class="ml-lg-5 mt-3">
              <div
                v-for="categoryName in Object.keys(actions)"
                :key="categoryName"
              >
                <AlertActions
                  :categoryName="categoryName"
                  :categoryResponse="userResponseForCategory(categoryName)"
                  :surveyIsCompleted="surveyIsCompleted"
                  :updateAlertActionResponse="handleUpdateAlertActionResponse"
                  :userCanSelectAlertActions="userCanSelectAlertActions"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="alert.links"
          class="p-5 d-flex align-items-center justify-content-between"
        >
          <div class="hide-fullscreen">
            <AlertLinks
              v-if="alert.links"
              :links="alert.links"
              :groupName="selectedGroupName"
              :handleLinkRoute="onClickAlertLink"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
// import { inject } from 'vue'
import commonWidgetProps from '@/components/mixins/common-widget-props'
import AlertActions from './subcomponents/AlertActions.vue'
import AlertLinks from './subcomponents/AlertLinks.vue'
import { requestTypes } from '@/constants/request-types'
import { errorTypes } from '@/constants/error-types'
import CategoryDetails from '@/components/widgets/categories-bars-widget/subcomponents/CategoryDetails.vue'
import WidgetBody from '@/components/widgets/shared/WidgetBody.vue'
import WidgetHeader from '@/components/widgets/shared/WidgetHeader.vue'

export const alertWidgetInformation = ({
  colData,
  userIsParticipatingInSurvey,
  userCanSelectAlertActions,
  userHasConfirmedActions
}) => {
  const tags = colData.tags.reduce((acc, tag) => {
    acc[tag.name] = tag
    return acc
  }, {})

  // get categories from tag.actionCategories array
  let categories = colData.tags.reduce((acc, tag) => {
    tag.actionCategories?.forEach((category) => {
      acc[category] = true
    })
    return acc
  }, {})

  const isHarassmentTag =
    Object.keys(tags || {}).length === 1 && tags.harassment
  if (isHarassmentTag) {
    categories = {
      ...categories,
      [tags.harassment?.links?.[0].route.params.categoryName]: {}
    }
  }
  const requests = {
    [requestTypes.SURVEY_DETAILS]: {},
    [requestTypes.TAGS]: { filter: tags }
  }
  if (isHarassmentTag) {
    requests[requestTypes.QUESTIONS] = {}
    requests[requestTypes.TREND_QUESTIONS] = {}
  }
  if (categories) {
    requests.updateResponses = {}
    requests[requestTypes.CATEGORIES] = { filter: categories }
    if (userIsParticipatingInSurvey) {
      requests[requestTypes.RESPONSES] = {}
    }
  }
  if (userCanSelectAlertActions || userHasConfirmedActions) {
    requests[requestTypes.FILTERED_SURVEY_DETAILS] = {}
  }
  return { requests }
}

export const alertWidgetSkeleton = () => {
  return {
    body: [
      {
        cols: [{ height: 2 }]
      },
      {
        cols: [{ height: 4 }]
      }
    ]
  }
}

const AlertWidget = {
  name: 'AlertWidget',
  components: {
    AlertActions,
    AlertLinks,
    CategoryDetails,
    WidgetBody,
    WidgetHeader
  },
  mixins: [commonWidgetProps],
  props: {
    categories: {
      type: Object,
      default: () => {}
    },
    surveyDetails: {
      type: Object,
      default: () => {}
    },
    filteredSurveyDetails: {
      type: Object,
      default: () => {}
    },
    tags: {
      type: Object,
      default: () => {}
    },
    theme: {
      type: String,
      default: 'default'
    },
    trendQuestions: {
      type: Object,
      default: () => {}
    },
    questions: {
      type: Object,
      default: () => {}
    },
    responses: {
      type: Array,
      default: () => []
    },
    updateResponses: {
      type: Function,
      default: () => {}
    },
    updateWidgetRequests: {
      type: Function,
      default: () => null
    },
    userCanSelectAlertActions: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dataForCategoryDetailsWidget: null,
      loadingQuestionResponses: {}
    }
  },
  mounted() {
    if (!this.alert || this.shouldHideDueToHarassmentNotInCurrentSurvey) {
      this.deregisterWidget({
        id: this.id,
        info: {
          reason: errorTypes.NOT_ENOUGH_DATA,
          hideWidget: true,
          hideWidgetWhenNoSurveyResults: true
        },
        groupName: this.selectedGroupName
      })
    }
  },
  computed: {
    actions() {
      const actionsMap = {}

      this.firstValidAlertData?.actionCategories?.forEach((categoryName) => {
        const category = (
          this.surveyDetailsForAlert.details.categories || []
        ).find((c) => c.name === categoryName)
        if (category) {
          const feedback = category.feedbacks.find(
            (f) => f.type === this.firstValidAlertData.resultType
          )
          actionsMap[categoryName] = feedback.details.actions.filter(
            (a) => a.type === 'proposal'
          )
        }
      })
      return actionsMap
    },
    alertActionsLoading() {
      return (
        this.userCanSelectAlertActions &&
        this.loading[requestTypes.FILTERED_SURVEY_DETAILS]
      )
    },
    alert() {
      const alertData = this.firstValidAlertData
      if (
        (!this.surveyDetailsForAlert || !alertData) &&
        !this.isHarassmentAlertOnly
      ) {
        null
        // return {
        //   details: 'lorem',
        //   feedback: this.$t('widgetAlert.notEnoughData'),
        //   type: 'info'
        // }
      } else if (alertData) {
        return {
          alert: this.$tr(
            this.surveyDetails.details.messages,
            `tags.${alertData.name}.results.${alertData.resultType}.alert`
          ),
          detailed: this.$tr(
            this.surveyDetails.details.messages,
            `tags.${alertData.name}.results.${alertData.resultType}.detailed`
          ),
          feedback: this.$tr(
            this.surveyDetails.details.messages,
            `tags.${alertData.name}.results.${alertData.resultType}.feedback`
          ),
          type: alertData.resultType,
          icon: this.getAlertIcon(alertData.resultType),
          links: alertData.links
        }
      } else {
        return null
      }
    },
    averages() {
      return {
        ...this.averageForItem('tags'),
        ...this.averageForItem('categories')
      }
    },
    surveyDetailsForAlert() {
      if (this.userCanSelectAlertActions) {
        return this.filteredSurveyDetails
      } else {
        return this.surveyDetails
      }
    },
    firstValidAlertData() {
      if (
        this.surveyDetailsForAlert &&
        this.selectedGroupName &&
        this.averages
      ) {
        return this.firstValidResultRule({
          rules: this.rules,
          averages: this.averages,
          surveyDetails: this.surveyDetailsForAlert,
          selectedGroupName: this.selectedGroupName,
          organizationName: this.organizationName
        })
      }
      return null
    },
    harassmentInCurrentSurvey() {
      if (this.isHarassmentAlertOnly && this.surveyIsAggregate) {
        const harassmentTag = this.tags.harassment
        return harassmentTag?.surveyNames.includes(this.surveyDetails.name)
      } else {
        return null
      }
    },
    shouldHideDueToHarassmentNotInCurrentSurvey() {
      return (
        this.isHarassmentAlertOnly &&
        this.surveyIsAggregate &&
        !this.surveyIsContainerAggregate &&
        !this.harassmentInCurrentSurvey
      )
    },
    isHarassmentAlertOnly() {
      return (
        this.rules &&
        this.rules.length === 1 &&
        (this.rules[0].name === 'harassment' ||
          this.rules[0].name === 'harassment-individual')
      )
    },
    isIndividualGroup() {
      return this.selectedGroup?.numberOfUsers === 1
    },
    rules() {
      return this.data.rules || this.data.tags
    },
    shouldDisplayActions() {
      return this.firstValidAlertData?.actionCategories?.length
    },
    surveyIsCompleted() {
      return this.surveyDetails.details.status === 'completed'
    }
  },
  methods: {
    averageForItem(statItemType) {
      const items = this[statItemType]
      return Object.keys(items || {}).reduce((result, statItemName) => {
        const statItem = items[statItemName]
        const hasValue = Object.keys(statItem).includes('value')
        // currentAverage && Object.keys(currentAverage).includes('value')
        if (hasValue) {
          let currentValue = statItem.value
          if (statItem.npsData) {
            currentValue = statItem.npsData.score
          }
          result[statItemName] = currentValue
        }
        return result
      }, {})
    },
    back() {
      this.dataForCategoryDetailsWidget = null
    },
    currentGroupHasFewerMembersThan({
      surveyDetails,
      maximumGroupMembers,
      selectedGroupName,
      organizationName
    }) {
      if (selectedGroupName === organizationName) {
        return surveyDetails.details.userCount <= maximumGroupMembers
      } else {
        const groupSettings = surveyDetails.details.groups.find(
          (group) => group.name === selectedGroupName
        )
        return groupSettings.numberOfUsers <= maximumGroupMembers
      }
    },
    firstValidResultRule({
      rules,
      averages,
      surveyDetails,
      selectedGroupName,
      organizationName
    }) {
      const validCondition = (rules || []).find((rule) => {
        const {
          operator,
          value,
          maximumGroupMembers,
          oneOf,
          onlyTriggerForMostRecentSurvey
        } = rule.condition || {}
        const hasMaximumGroupMemberRequirement =
          Number.isInteger(maximumGroupMembers)

        const okForMostRecentSurveyRule =
          !onlyTriggerForMostRecentSurvey ||
          (this.surveyIsAggregate &&
            this.itemIsInMostRecentSurveyForAggregate({
              itemName: rule.name
            }))
        if (!oneOf) {
          const itemValue = averages[rule.name]
          return (
            okForMostRecentSurveyRule &&
            this.operatorValue({ operator, value, itemValue }) &&
            (!hasMaximumGroupMemberRequirement ||
              this.currentGroupHasFewerMembersThan({
                surveyDetails,
                maximumGroupMembers,
                selectedGroupName,
                organizationName
              }))
          )
        } else if (oneOf && oneOf.length) {
          const atLeastOneItemMeetsCondition =
            oneOf.filter((item) => {
              const itemValue = averages[item]
              return (
                this.operatorValue({ operator, value, itemValue }) &&
                (!hasMaximumGroupMemberRequirement ||
                  this.currentGroupHasFewerMembersThan({
                    surveyDetails,
                    maximumGroupMembers,
                    selectedGroupName,
                    organizationName
                  }))
              )
            }).length > 0

          return (
            atLeastOneItemMeetsCondition &&
            !shouldHideDueToHarassmentNotInCurrentSurvey
          )
        }
      })

      if (validCondition) {
        return validCondition
      } else {
        return null
      }
    },
    getAlertIcon(type) {
      switch (type) {
        case 'info':
          return 'info-circle-fill'
        case 'low':
          return 'exclamation-triangle-fill'
        case 'medium':
          return 'info-circle-fill'
        case 'high':
          return 'star-fill'
        default:
          return 'exclamation-octagon-fill'
      }
    },
    itemIsInMostRecentSurveyForAggregate({ itemName }) {
      const mostRecentSurveyName = this.surveyDetails.allSurveysInfo
        .filter((s) => s.details.status === 'completed')
        .sort((a, b) => {
          return new Date(b.details.end_date) - new Date(a.details.end_date)
        })[0].name
      return this.tags[itemName].surveyNames.includes(mostRecentSurveyName)
    },
    onClickAlertLink(route) {
      if (route.name === 'ResultDetailsForGroupAndCategory') {
        const categoryWithDetails = this.surveyDetails.details.categories.find(
          (c) => c.name === route.params.categoryName
        )
        const questions = categoryWithDetails.questions.reduce((res, q) => {
          res[q.name] = {
            ...this.questions[q.name],
            name: q.name,
            averages: this.trendQuestions[q.name]?.averages || [],
            regressionValues:
              this.trendQuestions[q.name]?.regressionValues || []
          }

          return res
        }, {})
        this.dataForCategoryDetailsWidget = {
          ...this.categories[route.params.categoryName],
          name: route.params.categoryName,
          averages: [],
          regressionValues: [],
          questions
        }
      }
    },
    onLoadQuestionResponses(question) {
      this.loadingQuestionResponses = {
        ...this.loadingQuestionResponses,
        [question.name]: true
      }
      this.updateWidgetRequests({
        mode: 'add',
        id: this.id,
        requests: {
          [requestTypes.QUESTION_RESULTS_FOR_SURVEY]: {
            params: {
              [this.dataForCategoryDetailsWidget.name]: {
                [question.name]: {}
              },
              hideWidgetWhenLoading: false
            }
          }
        }
      })
    },
    onSwiperInit(swiper) {
      this.swiperInstance = swiper
    },
    onSelectCategoryTab() {
      this.updateSliderHeight()
    },
    updateSliderHeight() {
      setTimeout(() => {
        if (this.swiperInstance?.params) {
          this.swiperInstance.updateAutoHeight(200)
        }
      }, 25)
    },
    operatorValue({ operator, value, itemValue }) {
      return (
        (operator === 'greaterThan' && itemValue > value) ||
        (operator === 'lessThan' && itemValue < value) ||
        (operator === 'equalTo' && itemValue === value) ||
        (operator === 'lessThanOrEqual' && itemValue <= value) ||
        (operator === 'greaterThanOrEqual' && itemValue >= value) ||
        operator === 'always'
      )
    },
    surveyIsContainerAggregate() {
      return this.surveyDetails.details.aggregate?.config?.containerSurvey
    },
    userResponseForCategory(categoryName) {
      const userResponse = this.responses.find(
        (r) => r.categoryName === categoryName
      )
      if (!userResponse) {
        return {}
      }
      return {
        ...userResponse,
        actions: this.actions[categoryName].map((actionWithDetails) => ({
          ...userResponse.actions.find(
            (responseAction) => responseAction.name === actionWithDetails.name
          ),
          ...actionWithDetails,
          shouldDisplayInfoButton:
            actionWithDetails.messages.en.description &&
            actionWithDetails.messages.en.description !== 'null'
        }))
      }
    },
    handleUpdateAlertActionResponse({ action, categoryResponse }) {
      const categoryDetails = this.categories[categoryResponse.categoryName]
      const surveyName = this.surveyName
      const surveyTitle = this.$tr(this.surveyDetails.details.messages, 'title')
      const userGroups = this.surveyDetails.details.groups
        .filter((g) => {
          return categoryResponse.groups.includes(g.name)
        })
        .map((g) => ({
          name: g.name,
          access: g.access
        }))

      const params = {
        questions: categoryResponse.questions,
        resultRule: categoryResponse.resultRule,
        groups: userGroups,
        order: categoryResponse.order,
        actionDetails: {
          surveyName,
          surveyTitle,
          actions: {},
          categoryTitle: this.$tr(categoryDetails.messages, 'title'),
          locale: localStorage.selectedLocale || 'en'
        }
      }

      const actions = []
      categoryResponse.actions
        .filter((a) => a.value)
        .forEach((a) => {
          const cleanedMessages = Object.keys(a.messages).reduce(
            (res, locale) => {
              res[locale] = {}
              res[locale].actionTitle =
                a.messages[locale].title || a.messages.en.title
              res[locale].categoryTitle =
                categoryDetails.messages[locale].title ||
                categoryDetails.messages.en.title
              return res
            },
            {}
          )
          const messages = a.messages
          const actionName = a.name
          const actionTitle = this.$tr(messages, 'title') || actionName
          const actionDescription = this.$tr(messages, 'description')

          const completeActionTitle = `${
            actionDescription !== 'null' ? actionDescription : ''
          }${actionTitle}`
          params.actionDetails.actions[actionName] = {
            originalTitle: actionTitle,
            title: completeActionTitle,
            messages: cleanedMessages
          }
          actions.push({
            name: a.name,
            value: true,
            type: a.type,
            done: a.done || false
          })
        })
      params.actions = actions
      this.updateResponses({
        responses: [
          {
            action,
            categoryName: categoryResponse.categoryName,
            params
          }
        ]
      })
    }
  },
  watch: {
    questions(newQuestions) {
      if (this.dataForCategoryDetailsWidget) {
        Object.keys(this.loadingQuestionResponses).forEach((questionName) => {
          const questionWasLoading = this.loadingQuestionResponses[questionName]
          const newQuestion = newQuestions[questionName]
          if (
            questionWasLoading &&
            (newQuestion?.answers?.length || newQuestion?.error)
          ) {
            // mark question as no longer loading
            this.loadingQuestionResponses = {
              ...this.loadingQuestionResponses,
              [questionName]: false
            }
            // update the currentCategory questions with new answers
            this.dataForCategoryDetailsWidget.questions = {
              ...this.dataForCategoryDetailsWidget.questions,
              [questionName]: newQuestion
            }
            this.updateSliderHeight()
          }
        })
      }
    }
  }
}
export default AlertWidget
// export { AlertWidget as default }
</script>

<style lang="scss">
@use '../../../assets/scss/_brand.scss' as *;
.alert-box-low-default {
  border: 3px solid $brand-red;
}
.alert-box-medium-default {
  border: 3px solid $brand-yellow;
}
.alert-box-high-default {
  border: 3px solid $brand-green;
}
.alert-box-low-fun {
  border: 3px solid $brand-red;
}
.alert-box-medium-fun {
  border: 3px solid $brand-yellow;
}
.alert-box-high-fun {
  border: 3px solid $brand-green;
}
.alert-box-low-monochrome {
  border: 3px solid $brand-monochrome;
}
.alert-box-medium-monochrome {
  border: 3px solid $brand-monochrome;
}
.alert-box-high-monochrome {
  border: 3px solid $brand-monochrome;
}

.icon-low-default,
.icon-low-fun {
  color: $brand-red;
}
.icon-medium-default,
.icon-medium-fun {
  color: $brand-yellow;
}
.icon-high-default,
.icon-high-fun {
  color: $brand-green;
}

.icon-low-monochrome {
  color: $brand-monochrome !important;
}
.icon-medium-monochrome {
  color: $brand-monochrome !important;
}
.icon-high-monochrome {
  color: $brand-monochrome !important;
}
</style>
